import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Mail,
  Calendar,
  Edit3,
  X,
  Save,
  Phone,
  Camera,
  Upload,
  Trash2,
  Linkedin,
  Github,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/* -----------------------------------------------------
   BUTTON
------------------------------------------------------ */
const MotionButton = ({ active, icon, label, onClick, disabled, className = "", variant = "outline" }) => {
  let btnClasses = "";
  if (variant === "outline") {
    btnClasses = "border border-[#6366f1] text-[#6366f1] hover:bg-[#6366f1]/5";
  } else if (variant === "solid") {
    btnClasses = "bg-[#6366f1] text-white hover:bg-[#5046e5]";
  } else if (variant === "ghost") {
    btnClasses = "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200";
  }

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      whileHover={{ scale: 1.02, y: -0.5 }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-medium transition-all text-xs sm:text-sm ${className} ${btnClasses}`}
    >
      {icon}
      <span>{label}</span>
    </motion.button>
  );
};

/* -----------------------------------------------------
   MAIN CARD
------------------------------------------------------ */
const ProfileCard = ({
  candidateData,
  isEditing,
  editForm,
  setEditForm,
  handleEdit,
  handleSave,
  handleCancel,
  formatDate,
  updateLoading,
  subtitle,
  onPhotoUpload,
  onPhotoRemove,
  photoUploading,
}) => {
  const { t } = useTranslation();
  const [showPhotoMenu, setShowPhotoMenu] = useState(false);
  const photoMenuRef = useRef(null);
  const photoInputRef = useRef(null);

  const getUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    const apiUrl =
      process.env.REACT_APP_API_URL || `${window.location.origin}/api/`;
    return apiUrl.replace(/\/api\/?$/, "") + "/" + url.replace(/^\//, "");
  };

  const profileImageUrl = getUrl(candidateData?.profile?.current_photo);
  const userInitial = candidateData?.first_name?.[0]?.toUpperCase() || "C";

  // Close photo menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (photoMenuRef.current && !photoMenuRef.current.contains(e.target)) {
        setShowPhotoMenu(false);
      }
    };
    if (showPhotoMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPhotoMenu]);

  const handlePhotoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onPhotoUpload) {
      onPhotoUpload(file);
    }
    setShowPhotoMenu(false);
    // Reset file input so the same file can be re-selected
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handleRemovePhoto = () => {
    if (onPhotoRemove) {
      onPhotoRemove();
    }
    setShowPhotoMenu(false);
  };

  // Social links from candidateData
  const linkedinUrl = candidateData?.profile?.linkedin_url || "";
  const githubUrl = candidateData?.profile?.github_url || "";

  return (
    <div
      className="relative rounded-2xl p-6 bg-white border border-gray-100/80 shadow-[0_2px_12px_rgba(0,0,0,0.02)] transition-all duration-300"
    >
      {/* Hidden file input for photo upload */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoFileChange}
        className="hidden"
      />

      <div className="w-full">
        {/* HEADER / BASIC INFO */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start justify-between gap-4">
          <div className="flex items-center gap-4 text-left min-w-0">
            {/* AVATAR */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="relative flex-shrink-0"
              ref={photoMenuRef}
            >
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Profile"
                  className="w-20 h-20 rounded-full object-cover border border-gray-100 shadow-sm"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#8b5cf6] text-white flex items-center justify-center text-3xl font-semibold shadow-inner">
                  {userInitial}
                </div>
              )}
              {/* Camera icon overlay — clickable */}
              <button
                type="button"
                onClick={() => setShowPhotoMenu((prev) => !prev)}
                disabled={photoUploading}
                className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-[#4b5563] text-white flex items-center justify-center border-2 border-white shadow-sm cursor-pointer hover:bg-gray-700 transition-colors z-10 disabled:opacity-50"
                title={t('myDetails.profileCard.changePhoto', 'Change photo')}
              >
                {photoUploading ? (
                  <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-3.5 h-3.5" />
                )}
              </button>

              {/* Photo action popup menu */}
              <AnimatePresence>
                {showPhotoMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, y: -4 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: -4 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden min-w-[160px]"
                  >
                    <button
                      type="button"
                      onClick={() => photoInputRef.current?.click()}
                      className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                      <Upload className="w-4 h-4 text-[#6366f1]" />
                      {t('myDetails.profileCard.uploadPhoto', 'Upload Photo')}
                    </button>
                    {profileImageUrl && (
                      <button
                        type="button"
                        onClick={handleRemovePhoto}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium border-t border-gray-100"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t('myDetails.profileCard.removePhoto', 'Remove Photo')}
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* DETAILS */}
            <div className="min-w-0">
              <h2 className="text-xl font-bold text-gray-900 truncate">
                {candidateData.first_name} {candidateData.last_name}
              </h2>
              <p className="text-sm text-gray-500 font-medium mt-1 leading-normal">
                {subtitle || "Candidate Profile"}
              </p>
            </div>
          </div>

          {/* EDIT BUTTON (Only shown when NOT editing) */}
          {!isEditing && (
            <div className="flex-shrink-0 w-full sm:w-auto flex justify-center sm:justify-end">
              <MotionButton
                onClick={handleEdit}
                disabled={updateLoading}
                icon={<Edit3 className="w-4 h-4" />}
                label={t('myDetails.profileCard.editProfile')}
                variant="outline"
              />
            </div>
          )}
        </div>

        {/* CONTACT ROW AND JOIN DATE (Underneath) */}
        {!isEditing && (
          <div className="mt-3.5 space-y-1.5">
            {/* CONTACT ROW */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500 font-medium">
              <div className="flex items-center gap-1.5 min-w-0">
                <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="truncate">{candidateData.email}</span>
              </div>
              <span className="text-gray-300">|</span>
              <div className="flex items-center gap-1.5">
                <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span>{candidateData.profile.mobile || t('myDetails.profileCard.noMobile', 'Not provided')}</span>
              </div>
            </div>

            {/* SOCIAL LINKS ROW (if any exist) */}
            {(linkedinUrl || githubUrl) && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-500 font-medium">
                {linkedinUrl && (
                  <a
                    href={linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-[#0077b5] hover:underline"
                  >
                    <Linkedin className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate max-w-[180px]">{linkedinUrl.replace(/^https?:\/\/(www\.)?/i, '')}</span>
                  </a>
                )}
                {linkedinUrl && githubUrl && <span className="text-gray-300">|</span>}
                {githubUrl && (
                  <a
                    href={githubUrl.startsWith('http') ? githubUrl : `https://${githubUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-gray-700 hover:underline"
                  >
                    <Github className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate max-w-[180px]">{githubUrl.replace(/^https?:\/\/(www\.)?/i, '')}</span>
                  </a>
                )}
              </div>
            )}

            {/* JOIN DATE */}
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400 font-medium">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span>
                {t('myDetails.profileCard.memberSince', 'Member since {{date}}', { date: formatDate(candidateData.date_joined) })}
              </span>
            </div>
          </div>
        )}

        {/* EDIT FIELDS (Form shown when editing is active) */}
        {isEditing && (
          <div className="w-full mt-6 pt-6 border-t border-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField
                label={t('myDetails.profileCard.firstName')}
                value={editForm.first_name}
                onChange={(v) =>
                  setEditForm((p) => ({ ...p, first_name: v }))
                }
              />

              <InputField
                label={t('myDetails.profileCard.lastName')}
                value={editForm.last_name}
                onChange={(v) =>
                  setEditForm((p) => ({ ...p, last_name: v }))
                }
              />

              <InputField
                label={t('myDetails.profileCard.mobile')}
                value={editForm.mobile}
                full
                onChange={(v) => {
                  const digitOnly = v.replace(/\D/g, "");
                  const limited = digitOnly.slice(0, 12);
                  setEditForm((p) => ({ ...p, mobile: limited }));
                }}
              />

              <InputField
                label={t('myDetails.profileCard.linkedinUrl', 'LinkedIn URL')}
                value={editForm.linkedin_url || ""}
                placeholder="e.g. linkedin.com/in/your-profile"
                onChange={(v) =>
                  setEditForm((p) => ({ ...p, linkedin_url: v }))
                }
              />

              <InputField
                label={t('myDetails.profileCard.githubUrl', 'GitHub URL')}
                value={editForm.github_url || ""}
                placeholder="e.g. github.com/your-username"
                onChange={(v) =>
                  setEditForm((p) => ({ ...p, github_url: v }))
                }
              />
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <MotionButton
                onClick={handleCancel}
                disabled={updateLoading}
                icon={<X className="w-4 h-4" />}
                label={t('myDetails.profileCard.cancel')}
                variant="ghost"
              />
              <MotionButton
                onClick={handleSave}
                disabled={updateLoading}
                icon={<Save className="w-4 h-4" />}
                label={updateLoading ? t('myDetails.profileCard.saving') : t('myDetails.profileCard.saveChanges')}
                variant="solid"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* -----------------------------------------------------
   SMALL SUBCOMPONENTS
------------------------------------------------------ */

const InputField = ({ label, value, onChange, full, placeholder }) => (
  <div className={`${full ? "md:col-span-2" : ""}`}>
    <label className="text-xs font-semibold text-gray-400">{label}</label>
    <motion.input
      whileFocus={{ scale: 1.01 }}
      className="w-full mt-1.5 px-4 py-2.5 rounded-xl bg-gray-50/60 focus:bg-white border border-gray-150 focus:border-[#6366f1]/50 focus:ring-2 focus:ring-[#6366f1]/10 outline-none transition-all text-sm text-gray-800"
      value={value}
      placeholder={placeholder || ""}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default ProfileCard;